import PageStore from "csi/page/PageStore";
import { C8Node, RemoveEventHandle } from "csi/typings";
import aspect from "dojo/aspect";
import { action, computed, configure, makeObservable, observable, reaction } from "mobx";
import { SyncTrunk } from "mobx-sync";
import { Context, createContext } from "react";
import ThreeDStore from "./3D/threeDViewerStore";
import { rootCache } from "./Cache";
import WorkingCache from "./WorkingCache";
import BrowserStore from "./browser";
import CommentsStore from "./comments";
import FeatureTogglesStore from "./featureTogglesStore";
import LayoutStore from "./layout";
import LocalStore from "./local";
import RightPaneStore from "./rightPane";
import RouterStore from "./router";
import SessionStore from "./session";

configure({
	// TODO: Update all store updates to use action/runInAction
	// check computedRequiresReaction
	// check observableRequiresReaction
	//enforceActions: "observed",

	// NOTE: commented out as it can issue warnings if only some branches of code within a computed function use observables;
	// uncomment during development if interested to see any warnings
	//reactionRequiresObservable: true,
});

/**
 * Root mobx store that facilitates communication between various stores.
 * See https://mobx.js.org/best/store.html for details on mobx store best practices.  When future
 * stores are created, the references to those stores can be handled in a single place.  By passing
 * the root store to each individual store, stores can communicate with each other.
 */

export type StateCallback = (prop: string, oldValue: any, newValue: any) => void;

export class RootStore {
	@observable.ref private _commentsStore: CommentsStore;
	@observable.ref private _layoutStore: LayoutStore;
	@observable.ref private _pageStore: PageStore;
	private _pageContext: Context<PageStore>;
	@observable.ref private _threeDStore: ThreeDStore;
	@observable private _threeDReload: boolean = true;

	readonly browserStore = new BrowserStore();
	readonly featureTogglesStore = new FeatureTogglesStore();
	readonly localStore = new LocalStore();
	readonly rightPaneStore = new RightPaneStore(this);
	readonly routerStore = new RouterStore(this);
	readonly sessionStore = new SessionStore(this);

	/** store containing comments about current location object */
	@computed get commentsStore(): CommentsStore {
		return this._commentsStore;
	}

	/** store containing view layout for current location object */
	@computed get layoutStore(): LayoutStore {
		return this._layoutStore;
	}

	/** return true if layout is visible */
	get layoutVisible(): boolean {
		return !this.rightPaneStore.isFullWidth;
	}

	/** page context */
	get pageContext(): Context<PageStore> {
		return this._pageContext;
	}

	/** page store */
	get pageStore(): PageStore {
		return this._pageStore;
	}

	/** store containing view layout for current location object */
	@computed get threeDStore(): ThreeDStore {
		return this._threeDStore;
	}

	/** return true if user is in middle of changes that should stop browser unloading */
	@computed get isOperationInProgress(): boolean {
		const { browserStore, rightPaneStore, sessionStore } = this;
		return browserStore.editingInline ||
			!!sessionStore.modalView ||
			!!rightPaneStore.selected?.config.confirmLeaving?.();
	}

	/** working cache for current layout (or new if it does not exist) */
	@computed get workingCache(): WorkingCache {
		return this.layoutStore?.cache || new WorkingCache();
	}

	/** store flag to decide to reload 3d viewer or not while navigating to a new node */
	get threeDReload(): boolean {
		return this._threeDReload;
	}

	/** change 3d reload flag */
	@action setThreeDReload = (val: boolean): void => {
		this._threeDReload = val;
	};

	/** set comments store, making sure to cleanup existing store */
	@action setCommentsStore = (store: CommentsStore): void => {
		const { _commentsStore } = this;

		if (_commentsStore !== store) {
			_commentsStore?.cleanupReactions();
		}

		this._commentsStore = store;
	};

	/** set layout store */
	@action setLayoutStore = (store: LayoutStore): void => {
		const { _layoutStore } = this;

		if (_layoutStore !== store) {
			_layoutStore?.cleanupReactions();
		}

		this._layoutStore = store;
	};

	/** set pageStore and context */
	@action setPageStoreAndContext = <T extends PageStore>(pageStore: T): void => {
		// clean up the reactions for the previous store
		this._pageStore?.cleanupReactions();
		this._pageStore?.onPageStoreDestroy();

		this._pageStore = pageStore;
		this._pageContext = createContext(this._pageStore);

		this._pageStore?.onPageStoreCreate();
	};

	@action setThreeDStore = (store: ThreeDStore): void => {
		if (!this) {
			return;
		}

		const { _threeDStore } = this;
		if (_threeDStore !== store) {
			_threeDStore?.cleanupReactions();
		}

		this._threeDStore = store;
	};

	constructor() {
		makeObservable(this);
	}
}

export const rootStore = new RootStore();
export default rootStore;

export const {
	browserStore,
	featureTogglesStore,
	localStore,
	rightPaneStore,
	routerStore,
	sessionStore,
} = rootStore;

// any node added to rootCache should be added to resolver cache too
aspect.after(rootCache, "add", (node: C8Node, timestamp?: number) => {
	sessionStore.nonObservableCache.put(node, timestamp ? { timestamp } : null);
}, true);

// sync local store with local storage
const trunk = new SyncTrunk(localStore, {
	storageKey: "csiLocalState",
});

trunk.init();

/** function to observe a state property and return a handle that allows you to dispose of it */
export function watchStoreProperty(store: any, key: string, callback: StateCallback): RemoveEventHandle {
	const remove = reaction(() => store[key], (value: any, previousValue: any): void => {
		callback(key, previousValue, value);
	});

	return { remove };
}